import { useState } from "react";

/**
 * Phase 3.56 — Google Sign-In Button (Emergent-managed OAuth).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * Props:
 *   acceptedTerms (boolean) — only relevant on the Register page. When false, the
 *     button is disabled and shows an inline hint that AGB/Datenschutz must be
 *     accepted first. On the Login page this is hardcoded to `true`.
 *   mode ("login" | "register") — only used for the visible label.
 */
export default function GoogleAuthButton({ acceptedTerms = true, mode = "login" }) {
    const [redirecting, setRedirecting] = useState(false);

    const handleClick = () => {
        if (!acceptedTerms) return;
        setRedirecting(true);
        // Persist the terms flag so the AuthCallback can forward it to the
        // backend when the user actually completes the OAuth round-trip.
        try {
            sessionStorage.setItem("tw_google_accepted_terms", acceptedTerms ? "1" : "0");
        } catch { /* ignore */ }
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div>
            <button
                type="button"
                onClick={handleClick}
                disabled={!acceptedTerms || redirecting}
                className={`w-full h-12 inline-flex items-center justify-center gap-3 rounded-sm border-2 font-bold uppercase tracking-wider text-sm transition-colors ${acceptedTerms ? "bg-white border-[#111827] text-[#111827] hover:bg-gray-50" : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"}`}
                data-testid="google-login-btn"
                aria-label={mode === "register" ? "Mit Google registrieren" : "Mit Google anmelden"}
            >
                <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
                    <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.96H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z" fill="#FBBC05" />
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335" />
                </svg>
                {redirecting
                    ? "Weiterleitung …"
                    : mode === "register" ? "Mit Google registrieren" : "Mit Google anmelden"}
            </button>
            {!acceptedTerms && (
                <p className="mt-2 text-[11px] text-amber-700" data-testid="google-terms-required">
                    Bitte zuerst AGB und Datenschutz akzeptieren.
                </p>
            )}
        </div>
    );
}
